import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Poster from './components/Poster';
import posters from './data/posters';
import HomeBackButton from '../../components/HomeBackButton';
import bgImage from './bg.png';
import './MissingWallApp.css';

interface Position {
  x: number;
  y: number;
  tilt: number;
  delay: number;
}

const BASE_CARD = 400;
const MIN_CARD = 220;
const MAX_CARD = 420;
const CELL_PADDING = 24;

export default function MissingWallApp() {
  const gridRef = useRef<HTMLElement>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [cardSize, setCardSize] = useState<number>(BASE_CARD);
  const cardSizeRef = useRef<number>(BASE_CARD);
  const [isWindBlowing, setIsWindBlowing] = useState(false);
  const [openedPoster, setOpenedPoster] = useState<typeof posters[number] | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });
  const mouseDownTimeRef = useRef(0);
  const isDraggingRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    cardSizeRef.current = cardSize;
  }, [cardSize]);

  const computeLayout = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const { clientWidth: W, clientHeight: H } = el;
    const isMobile = W < 768;
    const num = posters.length;

    if (isMobile) {
      setCardSize(0);
      setPositions(posters.map(() => ({ x: 0, y: 0, tilt: 0, delay: 0 })));
      return;
    }

    let best = { cols: 1, rows: num, size: 0 };
    for (let cols = 1; cols <= num; cols++) {
      const rows = Math.ceil(num / cols);
      const size = Math.min(W / cols, H / rows);
      if (size > best.size) best = { cols, rows, size };
    }

    const cellW = W / best.cols;
    const cellH = H / best.rows;
    const fitSize = Math.min(cellW, cellH) - CELL_PADDING;
    const card = Math.max(MIN_CARD, Math.min(MAX_CARD, Math.floor(fitSize)));

    const placed: Position[] = [];
    for (let i = 0; i < num; i++) {
      const col = i % best.cols;
      const row = Math.floor(i / best.cols);

      const itemsInRow =
        row === best.rows - 1 ? num - row * best.cols : best.cols;
      const rowXOffset = ((best.cols - itemsInRow) * cellW) / 2;

      const cx = rowXOffset + col * cellW + cellW / 2;
      const cy = row * cellH + cellH / 2;

      const jitterMagX = Math.max(0, (cellW - card) / 2 - 8);
      const jitterMagY = Math.max(0, (cellH - card) / 2 - 8);
      const jx = (Math.random() - 0.5) * 2 * jitterMagX;
      const jy = (Math.random() - 0.5) * 2 * jitterMagY;

      const x = Math.max(4, Math.min(W - card - 4, cx + jx - card / 2));
      const y = Math.max(4, Math.min(H - card - 4, cy + jy - card / 2));

      const tilt = (Math.random() - 0.5) * 6;
      const delay = i * 0.12;
      placed.push({ x, y, tilt, delay });
    }
    setCardSize(card);
    setPositions(placed);
  }, []);

  useLayoutEffect(() => {
    computeLayout();
  }, [computeLayout]);

  useEffect(() => {
    let raf = 0;
    const handle = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeLayout);
    };
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('resize', handle);
      cancelAnimationFrame(raf);
    };
  }, [computeLayout]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.loop = true;
    a.muted = true;
    a.play().catch(() => {});
    setIsMuted(true);
  }, []);

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    const next = !isMuted;
    a.muted = next;
    setIsMuted(next);
    if (a.paused) {
      a.play().catch(() => {});
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsWindBlowing(true);
      setTimeout(() => {
        setIsWindBlowing(false);
      }, 4000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!openedPoster) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenedPoster(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openedPoster]);

  const wallStyle: React.CSSProperties = {
    ['--mw-bg' as any]: `url(${bgImage})`,
    ['--poster-size' as any]: cardSize ? `${cardSize}px` : undefined,
  };

  return (
    <div className="app-missing-wall" style={wallStyle}>
      <div className="wall">
        <section className="wall__random" ref={gridRef}>
          <audio ref={audioRef} src="/missing-wall/bg.mp3" autoPlay loop />
          {posters.map((poster, index) => {
            const p = positions[index] || { x: 24, y: 24, tilt: 0, delay: 0 };
            return (
              <div
                key={poster.name}
                className="poster-wrap"
                style={{
                  left: p.x,
                  top: p.y,
                  cursor: dragIndex === index ? 'grabbing' : 'grab',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!gridRef.current) return;
                  setDragIndex(index);
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  dragOffsetRef.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  };
                  dragMovedRef.current = false;
                  mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
                  mouseDownTimeRef.current = Date.now();
                  isDraggingRef.current = true;

                  const onMove = (me: MouseEvent) => {
                    if (!isDraggingRef.current) return;
                    const grid = gridRef.current;
                    if (!grid) return;
                    const { left, top, width, height } = grid.getBoundingClientRect();
                    const x = me.clientX - left - dragOffsetRef.current.x;
                    const y = me.clientY - top - dragOffsetRef.current.y;
                    const card = cardSizeRef.current || BASE_CARD;
                    const pad = 4;
                    const maxX = Math.max(pad, width - card - pad);
                    const maxY = Math.max(pad, height - card - pad);
                    dragMovedRef.current = true;
                    setPositions((prev) => {
                      const next = [...prev];
                      const cur = next[index] || { x: 24, y: 24, tilt: 0, delay: 0 };
                      next[index] = {
                        ...cur,
                        x: Math.min(Math.max(pad, x), maxX),
                        y: Math.min(Math.max(pad, y), maxY),
                      };
                      return next;
                    });
                  };
                  const onUp = (upEvent: MouseEvent) => {
                    upEvent.preventDefault();
                    const dx = Math.abs(upEvent.clientX - mouseDownPosRef.current.x);
                    const dy = Math.abs(upEvent.clientY - mouseDownPosRef.current.y);
                    const movedEnough = dx > 3 || dy > 3;
                    if (!movedEnough) {
                      dragMovedRef.current = false;
                    }
                    setDragIndex(null);
                    isDraggingRef.current = false;
                    window.removeEventListener('mousemove', onMove);
                    window.removeEventListener('mouseup', onUp);
                  };
                  window.addEventListener('mousemove', onMove);
                  window.addEventListener('mouseup', onUp);
                }}
              >
                <Poster
                  poster={poster}
                  index={index}
                  tilt={p.tilt}
                  shouldAnimate={isWindBlowing}
                  onOpen={() => setOpenedPoster(poster)}
                />
              </div>
            );
          })}
        </section>

        {openedPoster && (
          <div
            className="modal"
            onClick={() => setOpenedPoster(null)}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="modal__close"
              aria-label="Закрыть"
              onClick={(e) => {
                e.stopPropagation();
                setOpenedPoster(null);
              }}
            >
              ×
            </button>
            <div className="modal__content" onClick={(e) => e.stopPropagation()}>
              <Poster
                poster={openedPoster}
                index={0}
                tilt={0}
                shouldAnimate={false}
                isModal
              />
            </div>
          </div>
        )}

        <button
          className="audio-toggle"
          onClick={toggleAudio}
          aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
        >
          <img
            src={isMuted ? '/missing-wall/audio-off.png' : '/missing-wall/audio-on.png'}
            alt={isMuted ? 'Звук выключен' : 'Звук включен'}
            width={48}
            height={48}
          />
        </button>

        <HomeBackButton variant="dark" />
      </div>
    </div>
  );
}
